import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React from 'react';
import { UploadControl } from '@/components/UploadControl';
import { VideoFeatures, ThumbnailOverlay } from '@/store/videoFeatures';
import { useVideoFeaturesStore } from '@/store/videoFeatures/store';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Wand2, Info } from 'lucide-react';
import { ThumbnailButtonConfig } from './ThumbnailButtonConfig';
import { ThumbnailType } from './types';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TimeInput } from '@/components/ui/time-input';

interface PauseThumbnailTabProps {
  features: VideoFeatures;
  handlePauseThumbnailUpload: (file: File) => void;
  removeThumbnail: (type: ThumbnailType) => void;
  toggleButtonEnabled: (type: 'pauseThumbnail', enabled: boolean) => void;
  updatePauseThumbnailButton: (property: string, value: any) => void;
  updatePauseThumbnailMode: (mode: 'always' | 'timeRange') => void;
  updatePauseAt: (value: string) => void;
}

export const PauseThumbnailTab: React.FC<PauseThumbnailTabProps> = ({
  features,
  handlePauseThumbnailUpload,
  removeThumbnail,
  toggleButtonEnabled,
  updatePauseThumbnailButton,
  updatePauseThumbnailMode,
  updatePauseAt,
}) => {
  useLanguage();
  const setTempFile = useVideoFeaturesStore((state) => state.setTempFile);
  const clearTempFile = useVideoFeaturesStore((state) => state.clearTempFile);

  const handleFileUpload = (file: File) => {
    setTempFile('pauseThumbnailFile', file);
    handlePauseThumbnailUpload(file);
  };

  const handleRemove = (type: ThumbnailType) => {
    clearTempFile('pauseThumbnailFile');
    removeThumbnail(type);
  };


  return (
    <div className="space-y-6 pb-4">
      <UploadControl
        label=""
        accept="image/*"
        maxSize={20}
        preview={features.pauseThumbnail?.imageUrl}
        onFileSelected={handleFileUpload}
        onClear={() => handleRemove('pauseThumbnail')}
        isActive={features.pauseThumbnail?.enabled}
      />
      
      {features.pauseThumbnail?.enabled && (
        <>
          {/* Card: Modo de Exibição */}
          <Card className="border-[#E6E9F0] rounded-xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">{translateInterfaceText("Modo de Exibição da Thumbnail")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <RadioGroup
                value={features.pauseThumbnail?.pauseMode || 'always'}
                onValueChange={(value) => updatePauseThumbnailMode(value as 'always' | 'timeRange')}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="always" id="always-pause" />
                  <Label htmlFor="always-pause" className="text-sm font-normal cursor-pointer">
                    {translateInterfaceText("Sempre que pausar")}</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="timeRange" id="timerange-pause" />
                  <Label htmlFor="timerange-pause" className="text-sm font-normal cursor-pointer">
                    {translateInterfaceText("Apenas em um trecho específico")}</Label>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="inline-flex items-center justify-center w-4 h-4 rounded-full text-muted-foreground hover:text-primary transition-colors">
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="w-[220px] text-xs leading-relaxed p-3">
                        {translateInterfaceText("Quando o vídeo atingir o tempo definido, ele será pausado automaticamente e a thumbnail de pausa será exibida.")}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </RadioGroup>

              {/* Pause At Input */}
              {features.pauseThumbnail?.pauseMode === 'timeRange' && (
                <div className="mt-4 space-y-3">
                  <p className="text-[11px] text-[#6B7280] font-medium">
                    {translateInterfaceText("O vídeo será pausado automaticamente nesse tempo")}</p>
                  <div className="p-4 border border-[#E6E9F0] rounded-xl bg-[#F9FAFC]">
                    <div className="grid gap-2">
                      <Label htmlFor="pause-at-time" className="text-sm font-medium">{translateInterfaceText("Pausar em (min:seg)")}</Label>
                      <TimeInput
                        id="pause-at-time"
                        placeholder="01:30"
                        className="h-11 border-[#DDE3EE]"
                        value={features.pauseThumbnail?.timeRange?.start || ''}
                        onCommit={updatePauseAt}
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Card: Botão de Ação */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wand2
                    className="h-4 w-4 transition-colors duration-200"
                    style={{ color: !!features.pauseThumbnail?.actionButton ? '#1F6BFF' : '#BFC5D2' }}
                  />
                  <span>{translateInterfaceText("Botão de Thumb")}</span>
                </div>
                <Switch
                  id="pause-button"
                  checked={!!features.pauseThumbnail?.actionButton}
                  onCheckedChange={(checked) => toggleButtonEnabled('pauseThumbnail', checked)}
                />
              </CardTitle>
            </CardHeader>

            {features.pauseThumbnail?.actionButton && (
              <CardContent>
                <ThumbnailButtonConfig
                  thumbnail={features.pauseThumbnail}
                  onButtonPropertyChange={updatePauseThumbnailButton}
                />
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  );
};
